import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Gift, CheckCircle2, Loader2, User, Phone, Mail, ChevronRight, ArrowLeft } from "lucide-react";
import axios from "axios";
import { getUTMs } from "../../hooks/useUTMs";
import ConsentCheckboxes, { honeypotValue } from "./ConsentCheckboxes";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EBOOK_PDF_URL = "/downloads/O_Codigo_da_Riqueza.pdf";
const STORAGE_KEY = "exit_popup_shown";

const QUESTIONS = [
  {
    id: "objective",
    label: "Qual é o seu principal objetivo com investimentos?",
    options: [
      { value: "patrimonio",    label: "Construir patrimônio a longo prazo" },
      { value: "renda_passiva", label: "Gerar renda passiva" },
      { value: "imovel",        label: "Adquirir um imóvel" },
      { value: "diversificar",  label: "Diversificar meus investimentos" },
    ],
  },
  {
    id: "investment_range",
    label: "Qual é a sua faixa de investimento disponível?",
    options: [
      { value: "até 100k",    label: "Até R$ 100 mil" },
      { value: "100k-300k",   label: "R$ 100 mil a R$ 300 mil" },
      { value: "300k-500k",   label: "R$ 300 mil a R$ 500 mil" },
      { value: "500k-1M",     label: "R$ 500 mil a R$ 1 milhão" },
      { value: "acima de 1M", label: "Acima de R$ 1 milhão" },
    ],
  },
];

export default function ExitPopup() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ objective: "", investment_range: "" });
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const shown = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !shown.current) {
        shown.current = true;
        setVisible(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
        if (window.gtag) window.gtag("event", "view_item", { event_category: "exit_popup", event_label: "ebook_gratuito" });
        if (window.fbq) window.fbq("track", "ViewContent", { content_name: "Exit Popup Ebook Gratuito" });
      }
    };

    const show = () => {
      if (!shown.current && !sessionStorage.getItem(STORAGE_KEY)) {
        shown.current = true;
        setVisible(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
      }
    };

    // Em telas de toque não existe "saída" do mouse: abre depois que a pessoa
    // leu boa parte da página, em vez de interromper a leitura por tempo.
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (isTouch) {
      const handleScroll = () => {
        const scrolled = window.scrollY + window.innerHeight;
        if (scrolled >= document.documentElement.scrollHeight * 0.6) show();
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }

    const timer = setTimeout(show, 45000);
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(timer);
    };
  }, []);

  const handleClose = () => setVisible(false);

  const selectOption = (questionId, value) => {
    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);
    setTimeout(() => setStep((s) => s + 1), 260);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = EBOOK_PDF_URL;
    link.download = "O_Codigo_da_Riqueza_com_Consorcio.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Por favor, preencha seu nome completo."); return; }
    if (f.phone.replace(/\D/g, "").length < 10) { toast.error("Por favor, insira um WhatsApp válido com DDD."); return; }
    if (!f.email.trim() || !f.email.includes("@")) { toast.error("Por favor, insira um e-mail válido."); return; }
    if (!lgpdAccepted) { toast.error("Marque a autorização de contato para liberar o download."); return; }

    setLoading(true);

    const objectiveLabel = QUESTIONS[0].options.find((o) => o.value === answers.objective)?.label || answers.objective;
    const rangeLabel     = QUESTIONS[1].options.find((o) => o.value === answers.investment_range)?.label || answers.investment_range;

    const payload = {
      name: f.name.trim(),
      phone: f.phone.trim(),
      email: f.email.trim(),
      investment_range: answers.investment_range || "100k-300k",
      message: `Objetivo: ${objectiveLabel} | Faixa: ${rangeLabel} | LGPD: Autorizado | Origem: Exit Popup — O Código da Riqueza`,
      source: "ebook_popup",
      lgpd_accepted: true,
      marketing_consent: marketingAccepted,
      utm: getUTMs(),
      website: honeypotValue(e),
    };

    try {
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Download liberado com sucesso!");
      triggerDownload();
      if (window.gtag) window.gtag("event", "Lead", { event_category: "exit_popup", event_label: "ebook_download" });
      if (window.fbq) window.fbq("track", "Lead", { value: 0, currency: "BRL" });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;
  const progressPct = Math.min((step / totalSteps) * 100, 100);

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[9998]"
            onClick={handleClose}
            data-testid="exit-popup-overlay"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed inset-0 flex items-center justify-center z-[9999] p-4 pointer-events-none"
            data-testid="exit-popup"
          >
            <div className="relative bg-background border border-primary/40 shadow-gold rounded-xl max-w-md w-full p-6 sm:p-8 pointer-events-auto overflow-hidden">

              <button
                onClick={handleClose}
                data-testid="exit-popup-close"
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors text-muted-foreground hover:text-foreground cursor-pointer z-10"
              >
                <X size={15} />
              </button>

              {!submitted && (
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-white/5">
                  <motion.div
                    className="h-full"
                    style={{ background: "var(--gold-gradient)" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              )}

              {!submitted && (
                <div className="text-center mb-5">
                  <div className="w-11 h-11 rounded-full mx-auto mb-3 flex items-center justify-center bg-primary/10 border border-primary/30 text-primary">
                    <Gift size={20} />
                  </div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-primary uppercase mb-1">
                    Espere! Antes de sair
                  </p>
                  <h3 className="font-display text-lg sm:text-xl font-light leading-tight">
                    Baixe gratuitamente o guia{" "}
                    <span className="italic text-gold-gradient font-normal">"O Código da Riqueza"</span>
                  </h3>
                </div>
              )}

              <AnimatePresence mode="wait">

                {!submitted && step === 0 && (
                  <motion.div key="q0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.22 }}>
                    <QuizStep question={QUESTIONS[0]} selected={answers.objective} onSelect={(v) => selectOption("objective", v)} />
                  </motion.div>
                )}

                {!submitted && step === 1 && (
                  <motion.div key="q1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.22 }}>
                    <QuizStep question={QUESTIONS[1]} selected={answers.investment_range} onSelect={(v) => selectOption("investment_range", v)} onBack={goBack} />
                  </motion.div>
                )}

                {!submitted && step === 2 && (
                  <motion.div key="form" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.22 }}>
                    <p className="text-muted-foreground text-xs text-center mb-4 leading-relaxed">
                      Preencha seus dados para liberar o PDF gratuitamente.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-3 text-left">
                      <FieldInput icon={<User size={15} />} type="text" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Seu nome completo" />
                      <FieldInput icon={<Phone size={15} />} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: formatPhone(e.target.value) })} placeholder="WhatsApp com DDD" />
                      <FieldInput icon={<Mail size={15} />} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Seu melhor e-mail" />
                      
                      <div className="pt-1">
                        <ConsentCheckboxes
                          compact
                          idPrefix="popup"
                          contactText="Autorizo o contato por WhatsApp e e-mail para receber o material solicitado."
                          contact={lgpdAccepted}
                          onContactChange={setLgpdAccepted}
                          marketing={marketingAccepted}
                          onMarketingChange={setMarketingAccepted}
                        />
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <button type="button" onClick={goBack} className="w-9 h-9 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors cursor-pointer flex-shrink-0">
                          <ArrowLeft size={15} />
                        </button>
                        <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 flex-1 py-3 rounded-full font-medium text-xs sm:text-sm transition-all hover:translate-y-[-1px] shadow-gold cursor-pointer disabled:opacity-60" style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}>
                          {loading ? (<><Loader2 className="animate-spin" size={15} /> Liberando...</>) : (<>Baixar Ebook Gratuito <Download size={15} /></>)}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {submitted && (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
                    <CheckCircle2 className="text-primary mx-auto mb-3" size={36} />
                    <h3 className="font-display text-xl font-light mb-2">Download <span className="text-gold-gradient italic">liberado!</span></h3>
                    <p className="text-muted-foreground text-xs mb-5 leading-relaxed max-w-xs mx-auto">
                      O PDF foi enviado para o seu e-mail. Caso não apareça, verifique a pasta de spam.
                    </p>
                    <button onClick={triggerDownload} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-semibold shadow-gold hover:translate-y-[-1px] transition-all cursor-pointer" style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}>
                      <Download size={14} /> Baixar Novamente
                    </button>
                  </motion.div>
                )}

              </AnimatePresence>

              {!submitted && (
                <button onClick={handleClose} className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2 mt-4 w-full text-center cursor-pointer block">
                  Não, prefiro sair sem o material
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function QuizStep({ question, selected, onSelect, onBack }) {
  return (
    <div>
      <p className="text-sm font-medium text-center mb-4 leading-snug">{question.label}</p>
      <div className="flex flex-col gap-2">
        {question.options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border text-left text-xs sm:text-sm transition-all duration-150 cursor-pointer ${
                isSelected
                  ? "border-primary/70 bg-primary/10 text-foreground"
                  : "border-border bg-card-gradient text-muted-foreground hover:border-primary/40 hover:text-foreground hover:bg-white/5"
              }`}
            >
              <span>{opt.label}</span>
              {isSelected ? <CheckCircle2 size={15} className="text-primary flex-shrink-0" /> : <ChevronRight size={14} className="opacity-30 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-3 cursor-pointer">
          <ArrowLeft size={12} /> Voltar
        </button>
      )}
    </div>
  );
}

function FieldInput({ icon, type, value, onChange, placeholder }) {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">{icon}</div>
      <input type={type} required value={value} onChange={onChange} placeholder={placeholder} aria-label={placeholder} className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-card-gradient border border-input text-foreground placeholder:text-muted-foreground/60 text-base focus:outline-none focus:border-primary/70 transition-colors" />
    </div>
  );
}
